/**
 * Auth API client.
 *
 * Thin on purpose: it maps HTTP responses to a discriminated union the screens
 * switch on, and does nothing else. Every security-relevant decision was already
 * made by the backend — this file must not add to it or soften it.
 */

/**
 * What the sign-in call can produce.
 *
 * ┌─ `invalidCredentials` covers THREE backend cases ────────────────────────┐
 * │ The backend deliberately returns an identical 401 for a wrong password,  │
 * │ an unknown email, and a disabled account — see attemptLogin, which even  │
 * │ verifies a dummy argon2 hash for an unknown email so the TIMING matches  │
 * │ too.                                                                     │
 * │                                                                          │
 * │ THE UI MUST NOT UNDO THAT. There is no `unknownEmail` or                 │
 * │ `accountDisabled` variant here, because having one would invite a screen │
 * │ to render a distinguishing message and hand back the user-enumeration    │
 * │ oracle the backend spent effort closing.                                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export type SignInResult =
  | { kind: 'success'; expiresAt: string }
  | { kind: 'totpRequired' }
  | { kind: 'invalidCredentials' }
  | { kind: 'rateLimited'; retryAfterSeconds: number }
  | { kind: 'networkError' }

export type EnrollResult =
  | { kind: 'success'; secret: string; otpauthUrl: string }
  | { kind: 'alreadyEnabled' }
  | { kind: 'unauthenticated' }
  | { kind: 'networkError' }

export type ConfirmResult =
  | { kind: 'success' }
  | { kind: 'invalidCode' }
  | { kind: 'unauthenticated' }
  | { kind: 'networkError' }

export type CurrentUser = {
  id: string
  email: string
  firstName: string
  lastName: string
  totpEnabled: boolean
}

async function postJson(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // The session cookie is httpOnly, so the browser attaches it and JavaScript
    // never touches the token. `same-origin` is enough: Caddy serves the API and
    // the app from one origin.
    credentials: 'same-origin',
    body: JSON.stringify(body),
  })
}

export async function signIn(
  email: string,
  password: string,
  totpCode?: string,
): Promise<SignInResult> {
  try {
    const res = await postJson('/auth/login', { email, password, totpCode })

    if (res.status === 429) {
      const data = (await res.json().catch(() => ({}))) as { retryAfterSeconds?: number }
      // Fall back to the Retry-After header, then to 15 minutes, so the screen
      // can always show a concrete duration rather than "try again later".
      const header = Number(res.headers.get('retry-after'))
      return {
        kind: 'rateLimited',
        retryAfterSeconds: data.retryAfterSeconds ?? (Number.isFinite(header) ? header : 900),
      }
    }

    if (res.status === 401) return { kind: 'invalidCredentials' }

    if (res.ok) {
      const data = (await res.json()) as { status: string; expiresAt?: string }
      if (data.status === 'totp_required') return { kind: 'totpRequired' }
      return { kind: 'success', expiresAt: data.expiresAt ?? '' }
    }

    // 400 (missing fields) and anything unexpected. Deliberately reported as
    // invalid credentials rather than as a distinct error: the sign-in form has
    // no other failure a user can act on, and a separate message would be noise.
    return { kind: 'invalidCredentials' }
  } catch {
    return { kind: 'networkError' }
  }
}

/**
 * Who is signed in. **Three outcomes, and they were one until 2026-08-09.**
 *
 * ┌─ ⛔ THE THIRD OF THE 2026-08-04 CONSEQUENCES, AND THE LAST TO CLOSE ────────┐
 * │ `docs/INBOX-SCOPE-AND-EXIT.md` recorded it in these words: *"`fetchCurrent  │
 * │ User()` returning `null` conflates 401 expired, 401 never authenticated and │
 * │ NETWORK FAILURE. INBOX's data layer meets this on every request."*          │
 * │                                                                            │
 * │ The first two are separated elsewhere - `data/unauthorized.ts` reports a    │
 * │ refusal and `App.tsx` decides, because only it knows whether somebody was   │
 * │ on screen. **The third was still collapsed here**: a `catch` returning      │
 * │ `null` makes "the network is down" indistinguishable from "you are not      │
 * │ signed in", so a cold load on a dead connection shows the SIGN-IN FORM.     │
 * │                                                                            │
 * │ ⚠️ THAT IS A WRONG ANSWER DELIVERED CONFIDENTLY, and the worst possible     │
 * │ one: it invites somebody to type a password that cannot be sent. This is    │
 * │ the not-found-versus-cannot-look distinction on the first request the app   │
 * │ ever makes.                                                                 │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ A TAGGED UNION, NOT `CurrentUser | null | undefined`. A third nullish value
 * is the shape that gets collapsed back by the next `if (!user)` somebody
 * writes; a `kind` cannot be tested for truthiness by accident.
 */
export type CurrentUserResult =
  | { kind: 'signedIn'; user: CurrentUser }
  /** The server answered and nobody is signed in. A real, settled answer. */
  | { kind: 'signedOut' }
  /**
   * ⛔ THE SERVER DID NOT ANSWER. **We do not know who is signed in**, which is
   * not the same as knowing nobody is. Rendering the sign-in form here is the
   * collapse this type exists to prevent.
   */
  | { kind: 'unreachable' }

export async function fetchCurrentUserResult(): Promise<CurrentUserResult> {
  let res: Response
  try {
    res = await fetch('/auth/me', { credentials: 'same-origin' })
  } catch {
    // ⛔ ONLY A TRANSPORT FAILURE REACHES HERE. `fetch` rejects for DNS, TLS and
    // connection failures and NOT for a 4xx or 5xx, so this catch is precisely
    // "no answer" and nothing else. Widening it to wrap the whole body would
    // pull a JSON parse error in and re-collapse the distinction.
    return { kind: 'unreachable' }
  }

  // 401 is a real answer: the server heard us and says nobody is signed in.
  if (res.status === 401) return { kind: 'signedOut' }

  /**
   * ⚠️ A 5xx IS `unreachable`, NOT `signedOut`. The session may be perfectly
   * valid and the server unable to say so, and telling somebody to sign in
   * again because a backend restarted is how a working credential gets doubted.
   */
  if (!res.ok) return { kind: 'unreachable' }

  try {
    const data = (await res.json()) as { user: CurrentUser }
    return data.user ? { kind: 'signedIn', user: data.user } : { kind: 'signedOut' }
  } catch {
    // 200 with a body we cannot parse. The server is answering something that
    // is not this API - a proxy error page, most likely - which is closer to
    // unreachable than to signed out.
    return { kind: 'unreachable' }
  }
}

export async function signOut(): Promise<void> {
  try {
    await postJson('/auth/logout', {})
  } catch {
    // A failed logout still clears local state. The cookie may survive, but the
    // next request resolves it normally — refusing to sign out locally because
    // the network blipped would be worse.
  }
}

export async function beginTotpEnrollment(): Promise<EnrollResult> {
  try {
    const res = await postJson('/auth/totp/enroll', {})
    if (res.status === 401) return { kind: 'unauthenticated' }
    if (res.status === 409) return { kind: 'alreadyEnabled' }
    if (!res.ok) return { kind: 'networkError' }
    const data = (await res.json()) as { secret: string; otpauthUrl: string }
    return { kind: 'success', secret: data.secret, otpauthUrl: data.otpauthUrl }
  } catch {
    return { kind: 'networkError' }
  }
}

export async function confirmTotpEnrollment(code: string): Promise<ConfirmResult> {
  try {
    const res = await postJson('/auth/totp/confirm', { code })
    if (res.status === 401) return { kind: 'unauthenticated' }
    if (res.status === 400) return { kind: 'invalidCode' }
    if (!res.ok) return { kind: 'networkError' }
    return { kind: 'success' }
  } catch {
    return { kind: 'networkError' }
  }
}

/**
 * Password reset — NOT IMPLEMENTED, and deliberately absent from this client.
 *
 * The screens exist but their submit is disabled, so there is nothing to call.
 * No `requestPasswordReset` / `confirmPasswordReset` is defined here on purpose:
 * a function that 404s is worse than no function, because it looks callable.
 *
 * When SMTP lands, add them here against POST /auth/password-reset/request and
 * POST /auth/password-reset/confirm, and enable the screens. Tracked in
 * docs/TASKS.md §6 alongside the SMTP dependency.
 */
